import React from "react";
import "./assets/scss/theme.scss";
import { useRoutes } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { ToastContainer } from "react-toastify";
import { useDispatch } from "react-redux";
import useAuthStore from "./hooks/store/auth";
import usePermissions from "./hooks/usePermissions";
import getRoutes from "./routes";
import { createTheme } from "./theme";
import { bootstrapAccessContext, hydrateAccessContext } from "./store/auth";
import Loader from "./common/Loader";

import useThemeSettings from "./hooks/store/theme";
export const ColorModeContext = React.createContext({
  toggleColorMode: () => {},
});

const Routes = () => {
  const { role, capabilities, authBypassed } = usePermissions();
  const routes = React.useMemo(
    () => getRoutes({ role, capabilities, authBypassed }),
    [authBypassed, capabilities, role]
  );

  return useRoutes(routes);
};

export const getAccessBootstrapState = ({
  token,
  authBypassed,
  accessContextReady,
  env = process.env.NODE_ENV,
}) => {
  const hasAccessToken = Boolean(token);

  return {
    hasAccessToken,
    shouldBootstrapAnonymousAccess:
      env !== "production" &&
      !hasAccessToken &&
      !authBypassed &&
      !accessContextReady,
    shouldHydrateTokenAccess: hasAccessToken && !accessContextReady,
  };
};

function App() {
  const { primary } = useThemeSettings();
  const dispatch = useDispatch();
  const { token, authBypassed, accessContextReady } = useAuthStore();
  const hydratedTokenRef = React.useRef(null);
  const [isHydratingAccess, setIsHydratingAccess] = React.useState(false);
  const {
    hasAccessToken,
    shouldBootstrapAnonymousAccess,
    shouldHydrateTokenAccess,
  } = getAccessBootstrapState({
    token,
    authBypassed,
    accessContextReady,
  });

  const [mode, setMode] = React.useState("light");
  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
    }),
    []
  );

  React.useEffect(() => {
    let isActive = true;

    if (!hasAccessToken) {
      hydratedTokenRef.current = null;

      if (shouldBootstrapAnonymousAccess) {
        setIsHydratingAccess(true);
        Promise.resolve(dispatch(bootstrapAccessContext())).finally(() => {
          if (isActive) {
            setIsHydratingAccess(false);
          }
        });
      } else {
        setIsHydratingAccess(false);
      }

      return () => {
        isActive = false;
      };
    }

    if (accessContextReady) {
      hydratedTokenRef.current = token;
      setIsHydratingAccess(false);
      return () => {
        isActive = false;
      };
    }

    if (hydratedTokenRef.current === token) {
      return () => {
        isActive = false;
      };
    }

    hydratedTokenRef.current = token;
    setIsHydratingAccess(true);
    Promise.resolve(dispatch(hydrateAccessContext({ token }))).finally(() => {
      if (isActive) {
        setIsHydratingAccess(false);
      }
    });

    return () => {
      isActive = false;
    };
  }, [
    accessContextReady,
    dispatch,
    hasAccessToken,
    shouldBootstrapAnonymousAccess,
    token,
  ]);

  const shouldShowAccessLoader =
    isHydratingAccess ||
    shouldBootstrapAnonymousAccess ||
    shouldHydrateTokenAccess;

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: primary,
      },
    },
  });

  return (
    <>
      <HelmetProvider>
        <ColorModeContext.Provider value={colorMode}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Helmet titleTemplate="%s | " defaultTitle="Convio"></Helmet>
            {shouldShowAccessLoader ? (
              <Loader text="Loading access..." />
            ) : (
              <Routes />
            )}
            <ToastContainer
              position="top-right"
              autoClose={2000}
              hideProgressBar
              newestOnTop={false}
              closeOnClick
              pauseOnFocusLoss={false}
              draggable={false}
              pauseOnHover={false}
            />
          </ThemeProvider>
        </ColorModeContext.Provider>
      </HelmetProvider>
    </>
  );
}

export default App;
